import { ListboxButton, ListboxOption } from "@headlessui/react";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react"

import { AttributeRef } from "./AddAttribute";
import { ListBox } from "../../lib/fragments/ListBox";
import { generateUniqueKey } from "../../utils/Helpers";
import { AddAttributeProps } from "../../utils/Interfaces";
import { ToggleSwitch } from "../../components/utils/ToggleSwitch";
import { FloatingInput } from "../../components/utils/FloatingInput";
import { FloatingTextArea } from "../../components/utils/FloatingTextArea";
import { G_onInputChangeHandler, G_onInputBlurHandler } from "../../components/utils/InputHandlers"

export const TextAttribute = forwardRef<AttributeRef, AddAttributeProps>(({ whatToAdd, posting }, ref) => {
    const [state, setstate] = useState({
        httpStatus: 200,
        input: {
            name: '',
            subtitle: '',
            mandatory: false,
            multiline: false,
            restrictions: {
                name: "Alphanumeric",
                icon: "fa-input-text",
                desc: "Combination of letters, numbers, and symbols"
            },
        },
        errors: {
            name: '',
            subtitle: '',
            restrictions: '',
        }
    })

    const restrictionsBoxId = generateUniqueKey(17)
    const [restrictionsBoxWidth, setRestictionsBoxWidth] = useState(0)
    const [subtitleVisibility, setSubtitleVisibility] = useState(false)

    const restrictionsList = [
        { name: "Alphanumeric", icon: "fa-input-text", desc: "Combination of letters, numbers, and symbols" },
        { name: "Numerical", icon: "fa-input-numeric", desc: "Restricts input to numbers only" },
        { name: "URL", icon: "fa-globe-pointer", desc: "Restricts the input to a valid web link" },
    ]

    useEffect(() => {
        setRestictionsBoxWidth(document.getElementById(restrictionsBoxId).offsetWidth)
    }, [
        restrictionsBoxId,
        restrictionsBoxWidth,
        setRestictionsBoxWidth
    ]);

    const onChangeHandler = (e: any) => {
        if (!posting) {
            let { input } = state
            let { errors } = state
            let output: any = G_onInputChangeHandler(e)

            const updatedInput = {
                ...input,
                [e.target.name]: output.value
            };

            const updatedErrors = {
                ...errors,
                [e.target.name]: output.error
            };

            setstate((prev) => ({
                ...prev,
                input: updatedInput,
                errors: updatedErrors,
            }));
        }
    }

    const targetLength = (name: string) => {
        switch (name) {
            case 'subtitle':
                return {
                    min: 5,
                    max: 100
                };

            default:
                return {
                    min: 3,
                    max: 30
                };
        }
    }

    const onInputBlur = (e: any) => {
        if (!posting) {
            let { input } = state
            let { errors } = state

            const target0 = targetLength(e.target.name)
            let output: any = G_onInputBlurHandler(e, '', target0.min, target0.max)

            const updatedInput = {
                ...input,
                [e.target.name]: output.value
            };

            const updatedErrors = {
                ...errors,
                [e.target.name]: output.error
            };

            setstate((prev) => ({
                ...prev,
                input: updatedInput,
                errors: updatedErrors,
            }));
        }
    }

    const removeSubtitle = () => {
        setSubtitleVisibility(false)

        setstate((prev) => ({
            ...prev,
            input: {
                ...prev.input,
                subtitle: ''
            }, errors: {
                ...prev.errors,
                subtitle: ''
            }
        }))
    }

    const onListBoxHandler = (e: any, label: any) => {
        let { input } = state
        let updatedInput: any = {}

        if (e.name === 'Numerical' || e.name === 'URL') {
            updatedInput = {
                ...input,
                [label]: e,
                multiline: false,
            }
        } else {
            updatedInput = {
                ...input,
                [label]: e
            }
        }

        setstate((prev) => ({
            ...prev,
            input: updatedInput,
        }));
    }

    const handleCheckboxChange = (e: any) => {
        setstate((prev) => ({
            ...prev,
            input: {
                ...prev.input,
                [e.target.name]: e.target.checked
            }
        }))
    }

    useImperativeHandle(ref, () => ({
        integrityCheck: (e: any) => {
            let { input } = state
            const inputArray = Object.keys(input)

            let passedValidation = true
            let attributes = input

            inputArray.forEach((inputObject: any) => {
                let oTarget = e.target[inputObject]
                let event: any = {
                    target: {
                        name: inputObject,
                    }
                }

                switch (inputObject) {
                    case 'subtitle':
                        if (subtitleVisibility) {
                            event.target.value = oTarget.value.trim()
                            event.target.required = oTarget.required

                            onInputBlur(event)
                        }
                        break

                    case 'mandatory':
                    case 'multiline':
                    case 'restrictions':
                        event.target.value = input[inputObject]
                        event.target.required = false
                        break

                    default:
                        event.target.value = oTarget.value.trim()
                        event.target.required = oTarget.required

                        onInputBlur(event)
                        break;
                }
            })

            let { errors } = state
            const errorArray = Object.keys(errors)

            errorArray.forEach((errorObject) => {
                if (errors[errorObject].length > 0) {
                    passedValidation = false
                }
            })

            return {
                passedValidation,
                attributes
            }
        },
    }));

    return (
        <React.Fragment>
            <div className="w-full mb-3">
                <p className="text-lg text-slate-900 dark:text-white flex items-center font-medium gap-x-3 py-2">
                    <span className={`fa-light ${whatToAdd.icon} fa-lg`}></span>
                    {whatToAdd.name}
                </p>

                <p className="text-sm pb-1 pt-1">
                    Capture single or multi-line textual data such as names, descriptions and numerical data.
                </p>
            </div>

            <div className="w-full flex md:flex-row flex-col items-baseline md:gap-x-2">
                <div className="md:w-3/4 w-full">
                    <FloatingInput
                        name={"name"}
                        label={"Text to be captured"}
                        required={true}
                        posting={posting}
                        value={state.input.name}
                        error={state.errors.name}
                        blurHandler={onInputBlur}
                        changeHandler={onChangeHandler}
                    />
                </div>

                <div className="flex-grow md:flex justify-center w-full md:w-auto">
                    <button type="button" onClick={() => setSubtitleVisibility(true)} className={`text-theme pt-3 md:py-0 hover:text-orange-700 ${subtitleVisibility ? 'hidden' : 'block md:mb-0 mb-3'}`}>
                        <span className="text-sm">Add Subtitle</span>
                    </button>
                </div>
            </div>

            {
                subtitleVisibility ? (
                    <>
                        <div className="mt-3 md:py-0 md:pt-2 py-3 w-full bg-sky-00">
                            <FloatingTextArea
                                name={"subtitle"}
                                label={"Why is this data needed?"}
                                required={false}
                                posting={posting}
                                value={state.input.subtitle}
                                error={state.errors.subtitle}
                                blurHandler={onInputBlur}
                                changeHandler={onChangeHandler}
                            />

                            <div className="w-full md:w-auto flex flex-row-reverse">
                                <button type="button" onClick={() => removeSubtitle()} className={`text-theme md:py-0 hover:text-orange-700`}>
                                    <span className="text-sm">Remove Subtitle</span>
                                </button>
                            </div>
                        </div>
                    </>
                ) : null
            }

            <div className="w-full md:pt-3 pb-1">
                <div className="w-full pb-3">
                    <p className='label flex items-center text-slate-700 dark:text-slate-300 text-base'>
                        Restrictions
                    </p>

                    <p className='label items-center text-slate-500 dark:text-slate-400 text-sm'>
                        Define the type of data to capture
                    </p>
                </div>

                <div className="flex-none md:w-auto w-full pb-4">
                    <ListBox
                        width={restrictionsBoxWidth}
                        value={state.input.restrictions}
                        eventHandler={(e: any,) => onListBoxHandler(e, 'restrictions')}
                        button={
                            <>
                                <ListboxButton id={restrictionsBoxId} className="relative border md:w-52 w-full rounded-md py-2 pr-4 text-left cursor-default focus:ring-primary focus:border-primary sm:text-sm ring-slate-300 dark:ring-slate-600 focus:ring-orange-600 focus:outline-none focus:border-0 focus:ring-primary focus:outline-orange-500 border-slate-400 dark:border-slate-600 dark:text-white">
                                    <span className="flex w-full items-center align-middle">
                                        <div className="flex-grow flex items-center">
                                            <span className="truncate ml-3 font-normal group-data-[selected]:font-semibold">
                                                {
                                                    state.input.restrictions ? (
                                                        <div className="flex items-center gap-x-4">
                                                            <div className="w-6">
                                                                <span className={`fa-light fa-lg ${state.input.restrictions.icon}`}></span>
                                                            </div>

                                                            <span className="truncate group-data-[selected]:font-semibold">
                                                                {state.input.restrictions.name}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            -- Set restriction
                                                        </>
                                                    )
                                                }
                                            </span>
                                        </div>

                                        <span className="fa-light fa-chevron-down focus:text-primary float-end"></span>
                                    </span>
                                </ListboxButton>
                            </>
                        }
                        options={
                            <>
                                {
                                    restrictionsList.map((restiction: any) => (
                                        <ListboxOption key={restiction.icon} value={restiction} className="group relative cursor-default select-none py-2 pl-3 pr-9 data-[focus]:bg-[var(--focus-bg-light)] data-[focus]:text-[var(--color-secondary)] rounded-md">
                                            <div className="flex items-center gap-x-4">
                                                <div className="w-6">
                                                    <span className={`fa-light fa-lg ${restiction.icon}`}></span>
                                                </div>

                                                <span className="truncate group-data-[selected]:font-semibold">
                                                    {restiction.name}
                                                </span>
                                            </div>

                                            <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-orange-600 group-[&:not([data-selected])]:hidden group-data-[focus]:text-white">
                                                <span className="fa-light fa-check"></span>
                                            </span>
                                        </ListboxOption>
                                    ))
                                }
                            </>
                        }
                    />
                </div>

                <div className="w-full bg-orange-100 dark:bg-orange-700/50 px-2 text-theme-dark dark:text-orange-300 py-2 border-l-4 border-orange-400 text-sm">
                    {
                        state.input.restrictions ? (
                            <span className="text-wrap">
                                {state.input.restrictions.desc}
                            </span>
                        ) : (
                            <>
                                -- Set restriction
                            </>
                        )
                    }
                </div>
            </div>

            <div className="w-full py-4">
                <ToggleSwitch
                    label={'Multi-line Input'}
                    name={'multiline'}
                    disabled={state.input.restrictions.name === 'Numerical' ? true : false}
                    changeHandler={handleCheckboxChange}
                    checked={state.input.multiline}
                    description={"Allow users to write longer responses"}
                />
            </div>

            <div className="w-full py-4 border-t border-slate-300 dark:border-slate-600">
                <ToggleSwitch
                    label={'Mandatory'}
                    name={'mandatory'}
                    disabled={false}
                    changeHandler={handleCheckboxChange}
                    checked={state.input.mandatory}
                    description={"Ensures users provide a value before submitting."}
                />
            </div>
        </React.Fragment>
    )
})